import React, {
  ForwardRefRenderFunction,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from 'react'
import styled from 'styled-components'
import { ToolBar } from './ToolBar'
import { EditArea } from './EditArea'
import { TopicSetting } from './TopicSetting'
import { QuestionnaireContext } from './context'
import {
  FieldDomainEnum,
  FieldTypeEnum,
  TaskReq,
  fieldApi,
} from 'packages/sdks-next/growp'
import { DragDropContext, DropResult } from 'react-beautiful-dnd'
import {
  baseConfigQenerator,
  getAllUserFieldId,
  getUsedUserIds,
  groupsDataFormat,
  typeToLabel,
} from './utils'
import { ReqWithMetaData } from './types'
import { PreViewModal } from './Preview/PreviewModal'
import { xor } from '@seiue/util'
import { Toast } from '@seiue/ui'
import { AnyValueObj } from 'packages/types'
import { createUuid } from '@seiue/react-flow-builder'

/**
 * 问卷 - 创建|编辑
 *
 * @param param - 参数
 * @param param.questionnaires - 问卷结构数据
 * @param param.setQuestionnaires - 修改问卷结构数据方法
 * @param param.task - 任务数据
 * @param param.domain - 域名字段
 * @param param.goBack - 返回方法
 * @param param.structIsChange - 编辑状态下问卷结构被修改时调用
 * @param ref - ref
 * @returns React.ReactNode
 */
const Questionnaire: ForwardRefRenderFunction<
  React.ReactNode,
  {
    questionnaires: ReqWithMetaData[]
    setQuestionnaires: (v: ReqWithMetaData[]) => void
    task: TaskReq
    domain: FieldDomainEnum
    goBack?: () => void
    structIsChange?: () => void
  }
> = (
  { task, questionnaires, setQuestionnaires, domain, goBack, structIsChange },
  ref
) => {
  const [activeItemIndex, setActiveItemIndex] = useState<number | undefined>(
    undefined
  )
  const [isPreview, setIsPreview] = useState<boolean>(false)
  const timer = useRef<NodeJS.Timeout>()

  useEffect(() => {
    return () => {
      timer.current && clearTimeout(timer.current)
    }
  }, [])

  const { data } = fieldApi.useQueryFieldGroups({
    domain,
    query: { expand: ['questionnaireFields'] },
  })
  const formatGroups = groupsDataFormat(data || [])

  useImperativeHandle(ref, () => ({
    questionnaires,
    task,
  }))

  const onDragEnd = (result: DropResult) => {
    const { source, destination } = result
    if (!destination || destination.droppableId !== 'form') {
      return
    }

    const preIndex = source.index
    const nextIndex = destination.index
    if (source.droppableId === 'form') {
      if (preIndex === nextIndex) {
        return
      }
      const preQuestionnaires = questionnaires || []
      const dragItem = preQuestionnaires[preIndex]
      preQuestionnaires.splice(preIndex, 1)
      preQuestionnaires.splice(nextIndex, 0, dragItem)
      setQuestionnaires([...preQuestionnaires])
      setActiveItemIndex(undefined)
      // 改变题目顺序不视为改变问卷结构（已提交的数据不会被清空）
      // structIsChange && structIsChange()
      return
    }

    let type = result.draggableId
    let dragField: AnyValueObj = {}
    let isUsed = false
    if (!['基础组件', '辅助组件'].includes(source.droppableId)) {
      type = result.draggableId.split('-')[0]
      formatGroups.map((field) => {
        if (field.groupName === source.droppableId) {
          dragField = field.buttonConfigs[preIndex]

          const usedIds = getUsedUserIds(questionnaires)
          const allIds = getAllUserFieldId(data || [])
          const remainingIds = xor(usedIds, allIds)
          if (remainingIds.includes(dragField['id'])) {
            isUsed = true
          }
        }
      })
    }
    if (isUsed) {
      Toast.warning('同一用户字段只能使用一次')
      return
    }
    const preQuestionnaires = questionnaires || []
    const newItem = {
      uuid: createUuid(),
      ...baseConfigQenerator(type as FieldTypeEnum),
      type: type as FieldTypeEnum,
      label: typeToLabel.get(type as FieldTypeEnum),
      ...dragField,
      require: false,
      children: (type === FieldTypeEnum.Product
        ? [
            {
              ...baseConfigQenerator(FieldTypeEnum.String),
              label: typeToLabel.get(FieldTypeEnum.String),
            },
          ]
        : undefined) as any,
    }
    preQuestionnaires.splice(nextIndex, 0, newItem)
    setQuestionnaires([...preQuestionnaires])
    setActiveItemIndex(nextIndex)
    timer.current && clearTimeout(timer.current)
    timer.current = setTimeout(() => {
      const ele = document.getElementById(`${nextIndex}`)
      ele?.focus()
    }, 200)
    if (
      ![FieldTypeEnum.Describe, FieldTypeEnum.PageBreak].includes(
        type as FieldTypeEnum
      )
    ) {
      structIsChange && structIsChange()
    }
  }
  return (
    <Wrapper>
      <QuestionnaireContext.Provider
        value={{
          task,
          questionnaires,
          setQuestionnaires,
          activeItemIndex,
          setActiveItemIndex: (arr) => {
            setActiveItemIndex(arr)
          },
          domain,
          userFieldsConfig: formatGroups,
          setIsPreview: (status) => {
            setIsPreview(status)
          },
          allGroupIds: getAllUserFieldId(data || []),
          structIsChange,
        }}
      >
        {isPreview ? (
          <PreViewModal
            task={task}
            questionnaires={questionnaires}
            closePreview={() => setIsPreview(false)}
          />
        ) : (
          <DragDropContext onDragEnd={onDragEnd}>
            <>
              <LeftWrapper onClick={() => setActiveItemIndex(undefined)}>
                <ToolBar />
              </LeftWrapper>
              <MiddleWrapper onClick={() => setActiveItemIndex(undefined)}>
                <EditArea goBack={goBack} />
              </MiddleWrapper>
              <RightWrapper>
                <TopicSetting />
              </RightWrapper>
            </>
          </DragDropContext>
        )}
      </QuestionnaireContext.Provider>
    </Wrapper>
  )
}

export const QuestionnaireForward = React.forwardRef(Questionnaire)

const Wrapper = styled.div`
  width: 100%;
  height: calc(100vh - 120px);
  overflow: auto;
  display: flex;
  justify-content: space-between;
  background-color: ${(props) => props.theme.background._3};
`

const LeftWrapper = styled.div`
  width: 280px;
  min-width: 280px;
  height: calc(100vh - 120px);
  background-color: #ffffff;
`

const MiddleWrapper = styled.div`
  width: 840px;
  min-width: 840px;
  padding: 0 40px;
  height: calc(100vh - 120px);
  overflow-y: scroll;
  ::-webkit-scrollbar {
    display: none;
  }
`

const RightWrapper = styled.div`
  width: 320px;
  min-width: 320px;
  height: calc(100vh - 120px);
  background-color: #ffffff;
`
